import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import usePrevious from '@dustin-web/microsite-components/src/hooks/use-previous'
import { CartItemFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import {
  useLayout,
  useScrollPosition,
  useSiteContext,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'
import { slugify } from '@dustin-web/microsite-components/src/utils/urls'
import classNames from 'classnames'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import Icon from '@dustin-web/microsite-components/src/icon'
import { Animated } from '@dustin-web/microsite-components/src/animated'
import CloseButton from '@dustin-web/microsite-components/src/close-button/close-button'
import { OptimisticResponseCartItemId } from '@dustin-web/product-components/src/hooks/cart/cache-updater-cart'
import useShoppingCart from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'
import { CartNotificationAction } from '@dustin-web/microsite-components/src/utils/digital-data-types'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import { ProductWithCategoryType } from '@dustin-web/product-components/src/utils/digital-data-types'
import { isNotNullOrUndefined } from '@dustin-web/microsite-config/src/utils'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const CartNotification = () => {
  const { translate } = useTranslations()
  const { isAuthenticated } = useAuth()
  const { cartItems, lastUpdatedProductErpIdentifier, isUpdating, totalPriceExcludingVat } =
    useShoppingCart()
  const { currencyCode } = useSiteContext()
  const prevCartItems = usePrevious(cartItems)
  const componentRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const { header, responsive } = useLayout()
  const [product, setProduct] = useState<CartItemFragment>()
  const [inDom, setInDom] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const hoveringRef = useRef(isHovering)
  const isSmallScreen = !responsive.breakpoints.min.SM
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isTopOfPage, setIsTopOfPage] = useState(true)
  const { enabled: isCartNotificationGoToCheckout } = useFeatureFlag(
    'cart-notification-go-to-checkout'
  )

  const hideTimeout = useCallback(() => {
    const hide = setTimeout(
      () => {
        if (!hoveringRef.current) setIsVisible(false)
      },
      isSmallScreen ? 14000 : 8000
    )
    hideTimeoutRef.current = hide
  }, [setIsVisible])

  const handleClickOutside = (event: MouseEvent) => {
    const clickIsInside = componentRef.current?.contains(event.target as Element)

    if (!clickIsInside && isVisible) {
      setIsVisible(false)
    }
  }

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (isVisible) {
      setInDom(true)

      document.addEventListener('click', handleClickOutside, true)
      return () => {
        document.removeEventListener('click', handleClickOutside, true)
      }
    }
  }, [isVisible])

  useEffect(() => {
    hoveringRef.current = isHovering
    if (!isHovering) {
      hideTimeout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovering])

  useEffect(() => {
    const cartHasChanged =
      prevCartItems &&
      !prevCartItems.some(
        i =>
          i.productErpIdentifier === lastUpdatedProductErpIdentifier ||
          i.id === OptimisticResponseCartItemId
      )

    if (cartHasChanged) {
      const addedProduct = cartItems.find(
        i => i.productErpIdentifier === lastUpdatedProductErpIdentifier
      )

      if (addedProduct && !document.getElementById('mini-cart')) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
        }
        setProduct(addedProduct)

        setTimeout(() => {
          setIsVisible(true)
          hideTimeout()
        }, 300)
      }
    }
  }, [prevCartItems, cartItems, hideTimeout, setIsVisible, lastUpdatedProductErpIdentifier])

  // Get scroll depth to compensate for usb-bar thickness at top of page
  useScrollPosition(() => {
    window.scrollY === 0 ? setIsTopOfPage(true) : setIsTopOfPage(false)
  })

  const endUserInformationMissing = cartItems.some(cartItem =>
    cartItem.endUserInformation?.some(endUserInfo => !endUserInfo?.value)
  )

  const canAllBeDeliveredImmediately = cartItems.every(
    item => item.product?.availability?.availabilityStatus === 'IMMEDIATE_SHIPPING'
  )

  const showGoToCheckoutVariant = useMemo(
    () =>
      canAllBeDeliveredImmediately && !endUserInformationMissing && isAuthenticated && isVisible
        ? isCartNotificationGoToCheckout
        : false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canAllBeDeliveredImmediately, endUserInformationMissing, isAuthenticated, isVisible, inDom]
  )

  const sendGAEvent = (action: CartNotificationAction) => {
    DigitalData.pushCartNotificationEvent(action)
    if (action === 'go_to_checkout') {
      ProductDigitalData.pushBeginCheckout(
        cartItems
          .map((x, i) => {
            return x.product
              ? ({
                  ...x.product,
                  quantity: x.quantity,
                  productListIndex: i,
                } as ProductWithCategoryType)
              : null
          })
          .filter(isNotNullOrUndefined),
        'cart_notification',
        currencyCode,
        totalPriceExcludingVat
      )
    }
  }

  const displayName = product?.product?.displayName || ''

  return inDom && !isUpdating ? (
    <div
      className={classNames('u-fixed d-flex justify-content-end cart-notification-mobile', {
        container: !isSmallScreen,
      })}
      style={{
        top: isSmallScreen ? 'auto' : isTopOfPage ? header.height.px + 56 : header.height.px + 24,
        right: 0,
        bottom: isSmallScreen ? 0 : 'auto',
        left: 0,
        zIndex: 98,
        pointerEvents: 'none',
      }}>
      <Animated
        visible={isVisible}
        onHidden={() => setInDom(false)}
        animationType={isSmallScreen ? 'pull-below' : 'pull-right'}>
        <div onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
          <div className="d-flex flex-column c-box c-cart-notification" ref={componentRef}>
            <div className="d-flex align-items-center c-icon-text c-icon-text--md u-mb-16 ">
              <Icon name="shopping-cart-2" className="c-icon-text__icon" />{' '}
              <h5 className="u-mb-0">{translate('Added_To_Cart')}</h5>
            </div>
            <Tooltip tooltipText={displayName} className="c-icon-text__text u-mb-16 u-pr-32">
              <span className="c-cart-notification__text u-overflow-ellipsis">
                {product?.product?.displayName}
              </span>
            </Tooltip>
            <div className="d-flex flex-column-reverse flex-sm-row">
              {showGoToCheckoutVariant ? (
                <a
                  href="/checkoutsite"
                  className="c-btn u-bg-gray-02 u-color-black u-border u-mb-0 u-mr-0 u-mr-sm-16 flex-grow-1"
                  onClick={() => sendGAEvent('go_to_checkout')}>
                  {translate('Go_To_Checkout')}
                </a>
              ) : (
                <a
                  href={getProductUrl(
                    product?.product?.productErpIdentifier || '',
                    slugify(displayName) ?? '',
                    undefined,
                    true,
                    'accessories'
                  )}
                  className="c-btn u-mb-0 u-mr-0 u-mr-sm-16 flex-grow-1"
                  onClick={() => sendGAEvent('go_to_accessories')}>
                  {translate('Explore_Accessories')}
                </a>
              )}
              <a
                href="/shoppingcart"
                className="c-btn c-btn--checkout u-mb-16 u-mb-sm-0 flex-grow-1"
                onClick={() => sendGAEvent('go_to_cart')}>
                {translate('Go_To_Cart')}
              </a>
            </div>
            <CloseButton onClose={() => setIsVisible(false)} />
          </div>
        </div>
      </Animated>
    </div>
  ) : null
}

export default CartNotification
